/**
 * MediaDeleteDialog — choose which saved images go, then confirm once.
 * ───────────────────────────────────────────────────────────────────────────
 * The native `window.confirm("Permanently remove this image...?")` had three
 * problems the forms kept tripping over: it could not show WHICH image was
 * about to be destroyed, it could only ever ask about one image at a time (a
 * vendor clearing a logo, a banner and a card image answered three browser
 * prompts in a row), and it froze the page while the delete ran.
 *
 * This is the one replacement. It renders the candidates as thumbnails with a
 * checkbox each, pre-ticks the one whose Delete link was clicked, and hands the
 * chosen keys back through `onConfirm`. It is built ON ConfirmDialog rather
 * than beside it so the header, tone, busy state and footer are the same
 * everywhere in the dashboard.
 *
 * It knows nothing about vendors, products or Cloudinary. The wrappers do:
 *   VendorProfileMediaDeleteDialog  logo / shop banner / card image
 *   ProductMediaDeleteDialog        a dish's cover and extra angles
 *
 *   <MediaDeleteDialog
 *     show={!!target}
 *     title="Delete images from Taste Budz?"
 *     intro="They are removed from the vendor and from Cloudinary."
 *     items={[{ key: "logo", url, label: "Logo", hint: "588 x 558" }, ...]}
 *     initialKeys={["cardImage"]}
 *     busy={removing}
 *     error={partialFailureText}
 *     onConfirm={(keys) => remove(keys)}
 *     onCancel={() => setTarget(null)}
 *   />
 */
import React, { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

const styles = {
  intro: { marginBottom: 12 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: 12,
  },
  tile: (checked, disabled) => ({
    display: "block",
    margin: 0,
    borderRadius: 8,
    border: `2px solid ${checked ? "#b3261e" : "#dde3ea"}`,
    background: checked ? "#fdf3f2" : "#fff",
    padding: 6,
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.7 : 1,
    transition: "border-color 0.15s ease, background 0.15s ease",
  }),
  thumbWrap: (aspect) => ({
    position: "relative",
    width: "100%",
    aspectRatio: aspect,
    borderRadius: 5,
    overflow: "hidden",
    background: "#f1f4f8",
  }),
  thumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  check: {
    position: "absolute",
    top: 6,
    left: 6,
    width: 18,
    height: 18,
    accentColor: "#b3261e",
    cursor: "inherit",
    margin: 0,
  },
  label: {
    marginTop: 6,
    fontSize: 13,
    fontWeight: 600,
    color: "#17385a",
    lineHeight: 1.2,
  },
  hint: { fontSize: 11.5, color: "#6b7c93", marginTop: 2 },
  error: {
    marginTop: 12,
    marginBottom: 0,
    fontSize: 13,
    color: "#b3261e",
  },
  summary: { marginTop: 12, marginBottom: 0, fontSize: 13, color: "#4a5c6e" },
};

const MediaDeleteDialog = ({
  show,
  title = "Delete images?",
  intro,
  /** [{ key, url, label, hint, aspect }] - only images that actually exist. */
  items = [],
  /** Keys ticked when the dialog opens. Usually the one the operator clicked. */
  initialKeys = [],
  /** Noun for the confirm button: "Delete 1 image" / "Delete 3 images". */
  noun = "image",
  note = "This cannot be undone.",
  busy = false,
  error = "",
  onConfirm,
  onCancel,
}) => {
  const [selected, setSelected] = useState(() => new Set(initialKeys));

  /* Re-seed the ticks every time the dialog opens: the operator may have
     clicked a different Delete link than last time.

     `initialKeys` is a fresh array on every render, so the effect keys on a
     STABLE serialisation of it and rebuilds the set FROM that serialisation.
     That keeps the dependency list exhaustive with no eslint-disable
     directive — which matters more than it looks: this app pins eslint ^7
     while react-scripts 5 lints with eslint 8 + eslint-config-react-app, so
     the hoisted eslint 7 never registers the react-hooks plugin and a
     directive naming `react-hooks/exhaustive-deps` is an UNKNOWN RULE error
     that fails the Heroku build outright. It has done so three times now
     (Jul 2026, Aug 4 2026, Sep 13 2026). Never add one here; make the effect
     genuinely dependency-clean instead. `npm run lint` now refuses them too
     (scripts/checkBuildBlockers.mjs), because the lint config registers the
     plugin the real build lacks and so cannot see the problem by itself.

     JSON rather than join("|"): a key containing the separator would come
     back as two keys and tick the wrong images. */
  const initialSignature = JSON.stringify(initialKeys);
  useEffect(() => {
    if (show) setSelected(new Set(JSON.parse(initialSignature)));
  }, [show, initialSignature]);

  const multi = items.length > 1;
  const chosen = useMemo(
    () => items.filter((it) => selected.has(it.key)),
    [items, selected]
  );

  const toggle = (key) => {
    if (busy) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const count = chosen.length;
  const confirmLabel =
    count === 1 && !multi
      ? `Delete ${chosen[0].label ? chosen[0].label.toLowerCase() : noun}`
      : `Delete ${count} ${noun}${count === 1 ? "" : "s"}`;

  const body = (
    <>
      {intro && <p style={styles.intro}>{intro}</p>}
      <div style={styles.grid}>
        {items.map((it) => {
          const checked = selected.has(it.key);
          return (
            <label
              key={it.key}
              style={styles.tile(checked, busy)}
              aria-label={`${checked ? "Keep" : "Delete"} ${it.label || noun}`}
            >
              <div style={styles.thumbWrap(it.aspect || "16 / 10")}>
                {it.url ? (
                  <img src={it.url} alt={it.label || ""} style={styles.thumb} />
                ) : null}
                {multi && (
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={() => toggle(it.key)}
                    style={styles.check}
                  />
                )}
              </div>
              {it.label && <div style={styles.label}>{it.label}</div>}
              {it.hint && <div style={styles.hint}>{it.hint}</div>}
            </label>
          );
        })}
      </div>
      {multi && (
        <p style={styles.summary}>
          {count === 0
            ? "Nothing ticked. Tick at least one image to delete."
            : `${count} of ${items.length} selected.`}
        </p>
      )}
      {error && <p style={styles.error}>{error}</p>}
    </>
  );

  return (
    <ConfirmDialog
      show={show}
      tone="danger"
      title={title}
      body={body}
      note={note}
      confirmLabel={confirmLabel}
      busyLabel="Deleting..."
      busy={busy}
      confirmDisabled={count === 0}
      onConfirm={() => {
        if (!count || busy) return;
        if (onConfirm) onConfirm(chosen.map((it) => it.key));
      }}
      onCancel={onCancel}
    />
  );
};

export default MediaDeleteDialog;
